import { StandardEvents } from '@shopify/events';

if (!customElements.get('hb-cart-reminder')) {
 customElements.define('hb-cart-reminder', class extends HTMLElement {
  connectedCallback() {
   this.words = JSON.parse(this.querySelector('[data-translations]').textContent);
   this.stateKey = 'hb_saved_cart_v1';
   this.visitKey = 'hb_cart_visit_v1';
   this.storageAvailable = true;
   try {
    this.previous = JSON.parse(localStorage.getItem(this.stateKey) || 'null');
    this.visit = JSON.parse(sessionStorage.getItem(this.visitKey) || 'null');
   } catch { this.storageAvailable = false; }
   const now = Date.now();
   const navigation = performance.getEntriesByType('navigation')[0];
   let internal = false;
   try { internal = !!document.referrer && new URL(document.referrer).origin === location.origin; } catch {}
   const incoming = !internal && navigation?.type === 'navigate';
   this.newVisit = !this.visit || now - this.visit.lastSeen > 1800000 || incoming || (navigation?.type === 'back_forward' && !!this.visit?.departed);
   if (this.newVisit) this.visit = {lastSeen:now,shown:false};
   this.eligible = this.storageAvailable && this.newVisit && !!this.previous?.nonempty;
   this.visit.departed = false;
   this.persistVisit();
   this.close = () => { this.hidden = true; this.eligible = false; this.visit.shown = true; this.persistVisit(); };
   this.querySelector('[data-close]').addEventListener('click',this.close);
   this.onCart = event => {
    Promise.resolve(event.promise).then(() => this.refresh(false)).catch(() => {});
   };
   document.addEventListener(StandardEvents.cartLinesUpdate,this.onCart);
   this.onWake = event => {
    let stored;
    try { stored = JSON.parse(sessionStorage.getItem(this.visitKey) || 'null'); } catch {}
    if (event.type === 'pageshow' && (stored?.departed || this.visit.leftStore)) {
     this.visit.departed = false;
     this.visit.leftStore = false;
     this.visit.shown = false;
     this.eligible = !!this.previous?.nonempty;
    }
    if (document.visibilityState === 'visible') {
     if (Date.now() - this.visit.lastSeen > 1800000) {
      this.visit.shown = false;
      this.eligible = !!this.previous?.nonempty;
     }
     this.persistVisit();
     this.refresh(this.eligible);
    }
   };
   document.addEventListener('visibilitychange',this.onWake);
   window.addEventListener('pageshow',this.onWake);
   this.onLeaveLink = event => {
    const link = event.target.closest?.('a[href]');
    this.internalNavigation = !!link && new URL(link.href,location.href).origin === location.origin && !link.target && new URL(link.href,location.href).pathname !== location.pathname;
    if (link && new URL(link.href,location.href).origin !== location.origin) {
     this.visit.leftStore = true;
     this.persistVisit();
    }
   };
   document.addEventListener('click',this.onLeaveLink);
   this.onExit = () => { this.visit.departed = !this.internalNavigation; this.persistVisit(); };
   window.addEventListener('pagehide',this.onExit);
   this.heartbeat = setInterval(() => {
    if (document.visibilityState === 'visible') this.persistVisit();
   },15000);
   this.refresh(this.eligible);
  }
  persistVisit() {
   this.visit.lastSeen = Date.now();
   if (this.storageAvailable) {
    try { sessionStorage.setItem(this.visitKey,JSON.stringify(this.visit)); } catch { this.storageAvailable = false; }
   }
  }
  async refresh(mayShow) {
   if (this.loading) return;
   this.loading = true;
   try {
    const response = await fetch(`${this.dataset.root}cart.js`,{credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
    if (!response.ok) { this.hidden = true; return; }
    const cart = await response.json();
    const previous = this.previous;
    this.previous = {nonempty:cart.item_count > 0,total:cart.total_price,currency:cart.currency};
    if (this.storageAvailable) localStorage.setItem(this.stateKey,JSON.stringify(this.previous));
    if (!cart.item_count) {
     this.hidden = true;
     this.eligible = false;
     return;
    }
    if (/\/(cart|checkout|checkouts)(\/|$)/.test(location.pathname)) { this.hidden = true; return; }
    let favourites = [];
    try { favourites = JSON.parse(localStorage.getItem('hb_favourites_v1') || '[]'); } catch {}
    if (Array.isArray(favourites) && favourites.some(item => typeof item?.handle === 'string')) { this.hidden = true; return; }
    if (mayShow && !this.visit.shown && this.eligible) {
     await this.verifyProducts(cart);
     this.render(cart,previous);
     this.hidden = false;
     this.visit.shown = true;
     this.persistVisit();
     this.eligible = false;
    } else if (!this.hidden) { await this.verifyProducts(cart); this.render(cart,previous); }
   } catch { this.hidden = true; }
   finally { this.loading = false; }
  }
  async verifyProducts(cart) {
   const products = new Map();
   await Promise.all([...new Set(cart.items.map(item => item.handle))].map(async handle => {
    const response = await fetch(`${this.dataset.root}products/${encodeURIComponent(handle)}.js`,{credentials:'same-origin',cache:'no-store'});
    if (!response.ok) throw new Error('Product verification failed');
    products.set(handle,await response.json());
   }));
   for (const item of cart.items) {
    const variant = products.get(item.handle)?.variants?.find(variant => Number(variant.id) === Number(item.variant_id));
    item.hbUnavailable = !variant || !variant.available;
    if (variant && !item.selling_plan_allocation && variant.price !== item.original_price) throw new Error('Price verification failed');
   }
  }
  render(cart,previous) {
   const money = value => new Intl.NumberFormat(this.dataset.locale || 'en',{style:'currency', minimumFractionDigits:0, maximumFractionDigits:0, useGrouping:false,currency:cart.currency}).format(value/100);
   const saving = Math.max(0,cart.original_total_price - cart.total_price);
   const applications = [...(cart.cart_level_discount_applications || []),...cart.items.flatMap(item => (item.line_level_discount_allocations || []).map(a => a.discount_application))];
   const percentages = [...new Set(applications.filter(a => a?.value_type === 'percentage').map(a => Number(a.value)).filter(n => n > 0))];
   const effectivePercent = cart.original_total_price > 0 ? saving / cart.original_total_price * 100 : 0;
   const percent = percentages.length === 1 && applications.every(a => a?.value_type === 'percentage') && Math.abs(effectivePercent-percentages[0]) < 0.05 ? percentages[0] : null;
   const benefit = this.querySelector('[data-benefit]');
   benefit.textContent = saving ? (percent ? this.words.discount.replace('__PERCENT__',String(percent)).replace('__SAVING__',money(saving)) : this.words.saving.replace('__SAVING__',money(saving))) : this.words.saved;
   const before = this.querySelector('[data-before]');
   before.hidden = !saving;
   before.textContent = money(cart.original_total_price);
   this.querySelector('[data-after]').textContent = money(cart.total_price);
   this.querySelector('[data-checkout]').textContent = this.words.checkout;
   const titles = cart.items.map(item => item.product_title || item.title);
   const summary = this.querySelector('[data-summary]');
   summary.textContent = `${this.words.items.replace('__COUNT__',String(cart.item_count))} · ${titles.length === 1 ? titles[0] : `${titles[0]} + ${titles.length-1}`}`;
   if (cart.items[0].image) {
    const thumbnail = document.createElement('img');
    thumbnail.src = cart.items[0].image;
    thumbnail.alt = '';
    thumbnail.width = 36;
    thumbnail.height = 42;
    summary.prepend(thumbnail);
   }
   const items = this.querySelector('[data-items]');
   items.replaceChildren();
   for (const item of cart.items) {
    const row = document.createElement('div');
    row.className = 'hb-cart-return__item';
    if (item.image) {
     const image = document.createElement('img');
     image.src = item.image;
     image.alt = item.product_title || item.title;
     image.width = 44;
     image.height = 52;
     image.loading = 'lazy';
     row.append(image);
    }
    const text = document.createElement('div');
    const link = document.createElement('a');
    link.href = item.url;
    link.textContent = item.product_title || item.title;
    const options = document.createElement('small');
    const variant = (item.options_with_values || []).filter(option => option.value !== 'Default Title').map(option => `${option.name}: ${option.value}`).join(' · ');
    options.textContent = `${this.words.quantity}: ${item.quantity}${variant ? ` · ${variant}` : ''}`;
    text.append(link,options);
    if (item.hbUnavailable) { const warning = document.createElement('small'); warning.textContent = this.words.unavailable; text.append(warning); }
    row.append(text);
    items.append(row);
   }
   if (previous && previous.currency === cart.currency && previous.total !== cart.total_price) {
    const note = document.createElement('small');
    note.textContent = this.words.changed;
    items.append(note);
   }
  }
  disconnectedCallback() {
   clearInterval(this.heartbeat);
   document.removeEventListener(StandardEvents.cartLinesUpdate,this.onCart);
   document.removeEventListener('visibilitychange',this.onWake);
   document.removeEventListener('click',this.onLeaveLink);
   window.removeEventListener('pageshow',this.onWake);
   window.removeEventListener('pagehide',this.onExit);
   this.querySelector('[data-close]').removeEventListener('click',this.close);
  }
 });
}

